import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { FavoriteHeartButton } from '@/shared/components/FavoriteHeartButton'
import { PictureImg } from '@/shared/components/ui/picture-img'
import { useSubsidyMentionVisible } from '@/shared/api/brandQueries'
import { useReservationSubsidyEnabled } from '@/shared/hooks/useReservationSubsidyEnabled'
import { formatMoneyCents } from '@/shared/lib/formatMoney'
import { propertyCardImage } from '@/shared/lib/propertyCardImage'
import { homeShowcaseNightlyLabel } from '@/shared/lib/propertyPriceLabels'
import { resolveAssetUrl } from '@/shared/lib/resolveAssetUrl'
import type { HomePropertyTeaser } from '@/shared/api/homeShowcaseQueries'
import { CatalogCardBody } from '@/shared/components/CatalogCardLayout'
import { createLazyThemedIcon } from '@/shared/theme/resolveThemedIcon'

const MapPinIcon = createLazyThemedIcon('map-pin')
const HeartIcon = createLazyThemedIcon('heart')

export function HorizonHomePropertyCard({ property, priority = false }: { property: HomePropertyTeaser; priority?: boolean }) {
  const { t } = useTranslation()
  const subsidyEnabled = useReservationSubsidyEnabled()
  const subsidyMentionVisible = useSubsidyMentionVisible()
  const showSubsidyMention = subsidyEnabled && subsidyMentionVisible
  const cover = propertyCardImage(property)
  const jpeg = cover.jpeg ?? ''
  const webp = cover.webp ?? ''
  const src = jpeg || webp
  const currency = property.pricing.currency ?? 'EUR'
  const hasVariablePricing = property.pricing.has_variable_pricing === true
  const displayCents = hasVariablePricing
    ? (property.pricing.nightly_min_after_subsidy_cents ?? property.pricing.nightly_after_subsidy_cents)
    : property.pricing.nightly_after_subsidy_cents
  const detailTo = `/reservation/properties/${encodeURIComponent(property.id)}`

  return (
    <article className="group relative h-full overflow-hidden rounded-[var(--brand-radius-card)]">
      <Link
        to={detailTo}
        className="flex h-full flex-col text-inherit no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-ring)]"
        aria-label={property.name}
      >
        <div className="relative aspect-square overflow-hidden rounded-[var(--brand-radius-card)] bg-[var(--brand-surface-strong)] sm:aspect-[4/3]">
          {src ? (
            <PictureImg
              src={resolveAssetUrl(src)}
              webpSrc={webp && jpeg ? resolveAssetUrl(webp) : undefined}
              alt=""
              aria-hidden="true"
              className="size-full object-cover transition duration-200 group-hover:scale-[1.02]"
              loading={priority ? 'eager' : 'lazy'}
              decoding="async"
              sizes="(min-width: 1280px) 300px, (min-width: 640px) 50vw, 100vw"
              width={600}
              height={450}
            />
          ) : (
            <div className="flex size-full items-center justify-center text-[var(--brand-primary-strong)]">
              <MapPinIcon className="size-8" aria-hidden />
            </div>
          )}
          {property.is_featured ? (
            <span className="absolute left-3 top-3 inline-flex min-h-8 items-center gap-1.5 rounded-[var(--brand-radius-pill)] bg-[var(--brand-background)]/95 px-3 text-xs font-semibold text-[var(--brand-text-primary)]">
              <HeartIcon className="size-3.5 text-[var(--brand-primary-strong)]" aria-hidden />
              {t('home.showcase.featured_badge')}
            </span>
          ) : null}
        </div>
        <CatalogCardBody>
          <h3 className="truncate text-base font-semibold leading-snug text-[var(--brand-text-primary)]">{property.name}</h3>
          <p className="truncate text-sm text-[var(--brand-text-secondary)]">
            {t('home.showcase.city_country', {
              city: property.city ?? t('home.showcase.unknown_city'),
              country: property.country ?? t('home.showcase.unknown_country'),
            })}
          </p>
          <p className="text-sm font-semibold text-[var(--brand-text-primary)]">
            {homeShowcaseNightlyLabel(t, formatMoneyCents(displayCents, currency), {
              showSubsidyMention,
              hasVariablePricing,
              priceGranularity: property.price_granularity,
            })}
          </p>
        </CatalogCardBody>
      </Link>
      <FavoriteHeartButton propertyId={property.id} variant="overlay" />
    </article>
  )
}
